import { Capacitor } from "@capacitor/core";
import { env } from "@/shared/config/env";

/**
 * 앱 자동 업데이트 — Google Play 인앱 업데이트(In-App Updates).
 *
 * 경보를 받는 앱이라 **구버전이 조용히 남아 있는 것 자체가 위험**하다. 알림 채널이
 * 바뀌거나 경보 판정 로직이 고쳐졌는데 현장 단말만 옛 버전이면, 사용자는 앱이
 * 정상인 줄 알고 계속 쓴다. 그래서 두 단계로 나눈다.
 *
 *   · **강제(immediate)** — 서버가 알린 하한(`android_min_version_code`)보다 낮은
 *     버전이면 전체화면 업데이트를 띄우고, 끝나기 전에는 앱을 못 쓴다.
 *   · **권장(flexible)** — 그냥 새 버전이 있는 경우. 백그라운드로 받아두고 설치
 *     준비가 되면 조용히 적용한다. 사용을 막지 않는다.
 *
 * 웹(PWA)에서는 `isNativePlatform()` 이 false 라 전부 no-op이고, 플러그인은 동적
 * import 라 웹 번들에 섞이지 않는다.
 *
 * ⚠️ 인앱 업데이트는 **Play 스토어에서 설치된 앱에서만** 동작한다. 사이드로드한
 * 디버그 APK 에서는 `getAppUpdateInfo()` 가 실패하는데, 그건 정상이므로 조용히
 * 넘긴다 — 업데이트 확인 실패로 앱을 막지 않는다.
 */

/** 서버가 알려주는 앱 하한. 실패하면 0 = 강제 없음. */
async function fetchMinVersionCode(): Promise<number> {
  try {
    const res = await fetch(`${env.apiBase}/api/version`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return 0;
    const data = (await res.json()) as { android_min_version_code?: number } | null;
    const n = Number(data?.android_min_version_code ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export async function initAppUpdate(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (Capacitor.getPlatform() !== "android") return; // iOS 는 App Store 정책상 별도

  try {
    const { AppUpdate, AppUpdateAvailability } = await import(
      "@capawesome/capacitor-app-update"
    );

    const info = await AppUpdate.getAppUpdateInfo();
    if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) return;

    // 현재 versionCode 가 서버 하한 미만이면 강제.
    const current = Number(info.currentVersionCode ?? 0);
    const floor = await fetchMinVersionCode();
    const mustUpdate = floor > 0 && Number.isFinite(current) && current < floor;

    if (mustUpdate && info.immediateUpdateAllowed) {
      await AppUpdate.performImmediateUpdate();
      return;
    }

    if (info.flexibleUpdateAllowed) {
      // 백그라운드 다운로드 → 다 받아지면 설치까지 이어간다.
      await AppUpdate.addListener("onFlexibleUpdateStateChange", (state) => {
        // 5 = DOWNLOADED (Play InstallStatus). 숫자로 비교해 enum 변경에 덜 민감하게.
        if (Number(state.installStatus) === 5) {
          void AppUpdate.completeFlexibleUpdate().catch(() => {
            /* 사용자가 재시작을 미루면 다음 실행에서 다시 시도된다 */
          });
        }
      });
      await AppUpdate.startFlexibleUpdate();
      return;
    }

    // 즉시·유연 둘 다 막혀 있는데 하한 미만이면 스토어로 보낸다(마지막 수단).
    if (mustUpdate) await AppUpdate.openAppStore();
  } catch {
    /* 사이드로드·스토어 미설치·네트워크 실패 — 업데이트 확인 실패로 앱을 막지 않는다 */
  }
}
